import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, Button, Modal, Form } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { taskService } from '../../services/api';
import PerformanceMetrics from './PerformanceMetrics';
import './EmployeeDashboard.css';

const EmployeeDashboard = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [completionNotes, setCompletionNotes] = useState('');
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    const fetchTasks = async () => {
      try {
        setLoading(true);
        const response = await taskService.getAssignedTasks();
        setTasks(response.data?.data || []);
        setError('');
      } catch (err) {
        console.error('Error fetching tasks:', err);
        setError('Failed to load tasks. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (currentUser) {
      fetchTasks();
    }
  }, [currentUser]);

  // Calculate dashboard metrics
  const totalTasks = tasks.length;
  const completedTasks = tasks.filter(task => task.status === 'completed').length;
  const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
  
  // Calculate overdue tasks
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Set to start of day for accurate comparison
  const overdueTasks = tasks.filter(task => {
    if (!task.due_date || task.status === 'completed') return false;
    const dueDate = new Date(task.due_date);
    dueDate.setHours(0, 0, 0, 0); // Set to start of day for accurate comparison
    return dueDate < today;
  }).length;

  // Calculate tasks due soon (next 3 days)
  const threeDaysFromNow = new Date();
  threeDaysFromNow.setDate(today.getDate() + 3);
  const tasksDueSoon = tasks.filter(task => {
    if (!task.due_date) return false;
    const dueDate = new Date(task.due_date);
    return dueDate >= today && dueDate <= threeDaysFromNow && task.status !== 'completed';
  }).length;

  // Filter tasks by status for the kanban board
  const todoTasks = tasks.filter(task => 
    task.status === 'todo' || 
    task.status === '' || 
    task.status === 'pending'
  );
  
  const inProgressTasks = tasks.filter(task => 
    task.status === 'in_progress'
  );
  
  const awaitingApprovalTasks = tasks.filter(task => 
    task.status === 'awaiting_approval'
  );
  
  const completedTasksList = tasks.filter(task => 
    task.status === 'completed'
  );

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'awaiting_approval': return 'warning';
      case 'in_progress': return 'primary';
      case 'todo':
      case 'pending':
      case '': return 'secondary';
      default: return 'secondary';
    }
  };

  const getPriorityBadgeColor = (priority) => {
    switch (priority) {
      case 'low': return 'info';
      case 'medium': return 'warning';
      case 'high': return 'danger';
      case 'urgent': return 'danger';
      default: return 'secondary';
    }
  };

  const handleStartTask = async (taskId) => {
    try {
      setError(null);
      const currentTask = tasks.find(t => t.id === taskId);
      if (!currentTask) {
        throw new Error('Task not found');
      }

      // Make the API call first
      const response = await taskService.updateTaskStatus(taskId, { status: 'in_progress' });
      console.log('Start task response:', response);

      // Only update UI after successful API call
      setTasks(prevTasks => 
        prevTasks.map(task => 
          task.id === taskId 
            ? { ...task, status: 'in_progress' }
            : task
        )
      );
      
      // Show success message
      setSuccess('Task started successfully');
      setTimeout(() => setSuccess(null), 3000);

      // Refresh the task list to ensure we have the latest data
      const updatedTasks = await taskService.getAssignedTasks();
      setTasks(updatedTasks.data?.data || []);

      // Navigate to task details
      navigate(`/tasks/${taskId}`);
    } catch (err) {
      console.error('Error starting task:', err);
      setError(err.response?.data?.message || 'Failed to start task. Please try again.');
    }
  };

  const handleRequestCompletion = async (taskId) => {
    try {
      setError(null);
      const currentTask = tasks.find(t => t.id === taskId);
      if (!currentTask) {
        throw new Error('Task not found');
      }

      // Update UI optimistically
      setTasks(prevTasks => 
        prevTasks.map(task => 
          task.id === taskId 
            ? { ...task, status: 'awaiting_approval' }
            : task
        )
      );

      // Make the API call
      await taskService.requestTaskCompletion(taskId);
      
      // Show success message
      setSuccess('Task completion request submitted successfully');
      setTimeout(() => setSuccess(null), 3000);

      // Refresh the task list
      const updatedResponse = await taskService.getAssignedTasks();
      if (updatedResponse.data?.data) {
        setTasks(updatedResponse.data.data);
      }
    } catch (err) {
      console.error('Error requesting completion:', err);
      
      // Revert the optimistic update
      setTasks(prevTasks => 
        prevTasks.map(task => 
          task.id === taskId 
            ? { ...task, status: 'in_progress' }
            : task
        )
      );
      
      // Show error message
      setError(err.response?.data?.message || 'Failed to request completion. Please try again.');
    }
  };

  const renderTaskCard = (task) => (
    <Card 
      key={task.id} 
      className={`task-card mb-3 ${task.status === 'awaiting_approval' ? 'awaiting-approval' : ''}`}
      data-priority={task.priority}
      onClick={() => navigate(`/tasks/${task.id}`)} 
      style={{ cursor: 'pointer' }}
    >
      <Card.Body>
        <div className="d-flex justify-content-between align-items-start mb-2">
          <Card.Title className="mb-0">{task.title}</Card.Title>
          <Badge bg={task.priority === 'high' ? 'danger' : task.priority === 'medium' ? 'warning' : 'info'}>
            {task.priority}
          </Badge>
        </div>
        
        <Card.Text className="task-description">
          {task.description || 'No description provided.'}
        </Card.Text>
        
        {task.due_date && (
          <div className="mb-2">
            <small className="text-muted">
              Due: {new Date(task.due_date).toLocaleDateString()}
            </small>
          </div>
        )}
        
        <div className="d-flex justify-content-between align-items-center mt-3">
          <Badge bg={
            task.status === 'completed' ? 'success' :
            task.status === 'awaiting_approval' ? 'warning' :
            task.status === 'in_progress' ? 'primary' : 'secondary'
          }>
            {task.status === 'awaiting_approval' ? 'Awaiting Approval' : 
             task.status === '' || task.status === 'pending' ? 'To Do' : 
             task.status.charAt(0).toUpperCase() + task.status.slice(1).replace('_', ' ')}
          </Badge>
          
          <div>
            {(task.status === '' || task.status === 'todo' || task.status === 'pending') && (
              <Button 
                variant="primary" 
                size="sm" 
                className="me-2"
                onClick={(e) => {
                  e.stopPropagation();
                  handleStartTask(task.id);
                }}
              >
                <i className="fas fa-play me-1"></i> Start Task
              </Button>
            )}
            
            {task.status === 'in_progress' && (
              <Button 
                variant="warning" 
                size="sm" 
                className="me-2"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRequestCompletion(task.id);
                }}
              >
                <i className="fas fa-check-circle me-1"></i> Request Approval
              </Button>
            )}
            
            {task.status === 'awaiting_approval' && (
              <div className="text-warning">
                <i className="fas fa-clock me-1"></i> Waiting for approval
              </div>
            )}
          </div>
        </div>
      </Card.Body>
    </Card>
  );

  // Add CSS for awaiting approval tasks
  const style = document.createElement('style');
  style.textContent = `
    .task-card.awaiting-approval {
      border-left: 4px solid #ffc107;
    }
    .kanban-column-header {
      padding: 1rem;
      background-color: #f8f9fa;
      border-bottom: 2px solid #dee2e6;
      margin-bottom: 1rem;
    }
    .kanban-column-body {
      padding: 0.5rem;
      min-height: 200px;
    }
    .kanban-column {
      background-color: #fff;
      border-radius: 0.5rem;
      box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
      margin-bottom: 1rem;
    }
  `;
  document.head.appendChild(style);

  if (loading) {
    return <div className="text-center p-5">Loading tasks...</div>;
  }

  return (
    <Container fluid className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2><i className="fas fa-tasks me-2"></i>My Dashboard & Tasks</h2>
        <div>
          <Button 
            variant="outline-primary" 
            size="sm" 
            className="refresh-btn"
            onClick={() => {
              setLoading(true);
              const fetchTasks = async () => {
                try {
                  const response = await taskService.getAssignedTasks();
                  setTasks(response.data?.data || []);
                  setError('');
                } catch (err) {
                  setError('Failed to refresh tasks');
                } finally {
                  setLoading(false);
                }
              };
              fetchTasks();
            }}
          >
            <i className="fas fa-sync-alt me-1"></i> Refresh
          </Button>
        </div>
      </div>
      
      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* Task Statistics */}
      <Row className="mb-4">
        <Col md={12}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-light">
              <h5 className="mb-0"><i className="fas fa-chart-bar me-2"></i>My Performance</h5>
            </Card.Header>
            <Card.Body>
              <PerformanceMetrics
                totalTasks={totalTasks}
                completedTasks={completedTasks}
                inProgressTasks={inProgressTasks.length}
                todoTasks={todoTasks.length}
                tasksDueSoon={tasksDueSoon}
                completionRate={completionRate}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Kanban Board */}
      <Row>
        <Col md={3}>
          <div className="kanban-column todo-column">
            <div className="kanban-column-header">
              <h5>To Do <Badge bg="secondary" pill>{todoTasks.length}</Badge></h5>
            </div>
            <div className="kanban-column-body">
              {todoTasks.length === 0 ? (
                <div className="text-center text-muted p-4">No tasks to do</div>
              ) : (
                todoTasks.map(task => renderTaskCard(task))
              )}
            </div>
          </div>
        </Col>
        
        <Col md={3}>
          <div className="kanban-column progress-column">
            <div className="kanban-column-header">
              <h5>In Progress <Badge bg="primary" pill>{inProgressTasks.length}</Badge></h5>
            </div>
            <div className="kanban-column-body">
              {inProgressTasks.length === 0 ? (
                <div className="text-center text-muted p-4">No tasks in progress</div>
              ) : (
                inProgressTasks.map(task => renderTaskCard(task))
              )}
            </div>
          </div>
        </Col>

        <Col md={3}>
          <div className="kanban-column approval-column">
            <div className="kanban-column-header">
              <h5>Awaiting Approval <Badge bg="warning" pill>{awaitingApprovalTasks.length}</Badge></h5>
            </div>
            <div className="kanban-column-body">
              {awaitingApprovalTasks.length === 0 ? (
                <div className="text-center text-muted p-4">No tasks awaiting approval</div>
              ) : (
                awaitingApprovalTasks.map(task => renderTaskCard(task))
              )}
            </div>
          </div>
        </Col>
        
        <Col md={3}>
          <div className="kanban-column completed-column">
            <div className="kanban-column-header">
              <h5>Completed <Badge bg="success" pill>{completedTasksList.length}</Badge></h5>
            </div>
            <div className="kanban-column-body">
              {completedTasksList.length === 0 ? (
                <div className="text-center text-muted p-4">No completed tasks</div>
              ) : (
                completedTasksList.map(task => renderTaskCard(task))
              )}
            </div>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default EmployeeDashboard;
